import { apiService } from './api';
import { offlineStorageService } from './offlineStorage';

export interface SyncProgress {
  total: number;
  completed: number;
  current: string;
  errors: string[];
}

export interface SyncResult {
  success: boolean;
  syncedDonations: number;
  syncedReceipts: number;
  errors: string[];
}

class SyncService {
  private isSyncing = false;
  private syncListeners: ((progress: SyncProgress) => void)[] = [];

  // Aggiungi listener per il progresso di sincronizzazione
  addSyncListener(listener: (progress: SyncProgress) => void): void {
    this.syncListeners.push(listener);
  }

  // Rimuovi listener
  removeSyncListener(listener: (progress: SyncProgress) => void): void {
    this.syncListeners = this.syncListeners.filter(l => l !== listener);
  }

  // Notifica progresso a tutti i listener
  private notifyProgress(progress: SyncProgress): void {
    this.syncListeners.forEach(listener => listener(progress));
  }

  // Verifica se la sincronizzazione è in corso
  isCurrentlySyncing(): boolean {
    return this.isSyncing;
  }

  // Sincronizzazione completa
  async syncAll(): Promise<SyncResult> {
    if (this.isSyncing) {
      throw new Error('Sync already in progress');
    }

    this.isSyncing = true;
    const errors: string[] = [];
    let syncedDonations = 0;
    let syncedReceipts = 0;

    try {
      // Verifica connessione
      const isOnline = await apiService.isOnline();
      if (!isOnline) {
        throw new Error('No internet connection');
      }

      // Ottieni dati offline
      const [pendingDonations, pendingReceipts] = await Promise.all([
        offlineStorageService.getPendingDonations(),
        offlineStorageService.getPendingReceipts()
      ]);

      const total = pendingDonations.length + pendingReceipts.length;
      let completed = 0;

      // Sincronizza donazioni
      for (const offlineDonation of pendingDonations) {
        try {
          this.notifyProgress({
            total,
            completed,
            current: `Sincronizzando donazione ${offlineDonation.localId}`,
            errors
          });

          // Aggiorna status a SYNCING
          await offlineStorageService.updateDonationStatus(offlineDonation.localId, 'SYNCING');

          // Crea donazione sul server
          await apiService.createDonation(offlineDonation.data);

          // Aggiorna status a SYNCED
          await offlineStorageService.updateDonationStatus(offlineDonation.localId, 'SYNCED');
          syncedDonations++;
          completed++;

        } catch (error) {
          const errorMessage = `Errore sincronizzazione donazione ${offlineDonation.localId}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMessage);
          
          // Aggiorna status a ERROR
          await offlineStorageService.updateDonationStatus(offlineDonation.localId, 'ERROR', errorMessage);
          completed++;
        }
      }

      // Sincronizza ricevute
      for (const offlineReceipt of pendingReceipts) {
        try {
          this.notifyProgress({
            total,
            completed,
            current: `Sincronizzando ricevuta ${offlineReceipt.localId}`,
            errors
          });

          // Aggiorna status a SYNCING
          await offlineStorageService.updateReceiptStatus(offlineReceipt.localId, 'SYNCING');

          // Upload ricevuta sul server
          await apiService.uploadReceipt({
            file: offlineReceipt.file,
            donationId: offlineReceipt.donationId
          });

          // Aggiorna status a SYNCED
          await offlineStorageService.updateReceiptStatus(offlineReceipt.localId, 'SYNCED');
          syncedReceipts++;
          completed++;

        } catch (error) {
          const errorMessage = `Errore sincronizzazione ricevuta ${offlineReceipt.localId}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMessage);
          
          // Aggiorna status a ERROR
          await offlineStorageService.updateReceiptStatus(offlineReceipt.localId, 'ERROR', errorMessage);
          completed++;
        }
      }

      // Notifica completamento
      this.notifyProgress({
        total,
        completed,
        current: 'Sincronizzazione completata',
        errors
      });

      // Pulizia dati sincronizzati
      await offlineStorageService.cleanupSyncedData();

      return {
        success: errors.length === 0,
        syncedDonations,
        syncedReceipts,
        errors
      };

    } catch (error) {
      const errorMessage = `Errore durante la sincronizzazione: ${error instanceof Error ? error.message : 'Unknown error'}`;
      errors.push(errorMessage);
      
      return {
        success: false,
        syncedDonations,
        syncedReceipts,
        errors
      };
    } finally {
      this.isSyncing = false;
    }
  }

  // Sincronizzazione solo donazioni
  async syncDonations(): Promise<{ success: boolean; synced: number; errors: string[] }> {
    if (this.isSyncing) {
      throw new Error('Sync already in progress');
    }

    this.isSyncing = true;
    const errors: string[] = [];
    let synced = 0;

    try {
      const pendingDonations = await offlineStorageService.getPendingDonations();
      
      for (const offlineDonation of pendingDonations) {
        try {
          await offlineStorageService.updateDonationStatus(offlineDonation.localId, 'SYNCING');
          await apiService.createDonation(offlineDonation.data);
          await offlineStorageService.updateDonationStatus(offlineDonation.localId, 'SYNCED');
          synced++;
        } catch (error) {
          const errorMessage = `Errore donazione ${offlineDonation.localId}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMessage);
          await offlineStorageService.updateDonationStatus(offlineDonation.localId, 'ERROR', errorMessage);
        }
      }

      return { success: errors.length === 0, synced, errors };
    } finally {
      this.isSyncing = false;
    }
  }

  // Sincronizzazione solo ricevute
  async syncReceipts(): Promise<{ success: boolean; synced: number; errors: string[] }> {
    if (this.isSyncing) {
      throw new Error('Sync already in progress');
    }

    this.isSyncing = true;
    const errors: string[] = [];
    let synced = 0;

    try {
      const pendingReceipts = await offlineStorageService.getPendingReceipts();
      
      for (const offlineReceipt of pendingReceipts) {
        try {
          await offlineStorageService.updateReceiptStatus(offlineReceipt.localId, 'SYNCING');
          await apiService.uploadReceipt({
            file: offlineReceipt.file,
            donationId: offlineReceipt.donationId
          });
          await offlineStorageService.updateReceiptStatus(offlineReceipt.localId, 'SYNCED');
          synced++;
        } catch (error) {
          const errorMessage = `Errore ricevuta ${offlineReceipt.localId}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMessage);
          await offlineStorageService.updateReceiptStatus(offlineReceipt.localId, 'ERROR', errorMessage);
        }
      }

      return { success: errors.length === 0, synced, errors };
    } finally {
      this.isSyncing = false;
    }
  }

  // Sincronizzazione automatica (chiamata quando torna online)
  async autoSync(): Promise<void> {
    try {
      const isOnline = await apiService.isOnline();
      if (!isOnline) return;

      const stats = await offlineStorageService.getOfflineStats();
      if (stats.pendingDonations === 0 && stats.pendingReceipts === 0) return;

      // Sincronizza in background senza notifiche
      await this.syncAll();
    } catch (error) {
      console.warn('Auto sync failed:', error);
    }
  }

  // Forza sincronizzazione (con notifiche)
  async forceSync(): Promise<SyncResult> {
    return await this.syncAll();
  }

  // Ottieni statistiche offline
  async getOfflineStats() {
    return await offlineStorageService.getOfflineStats();
  }

  // Reset dati offline (per testing)
  async clearOfflineData(): Promise<void> {
    await offlineStorageService.clearAllData();
  }
}

export const syncService = new SyncService();
export default syncService;
